/**
 * Session Domain Model
 * Represents a study session
 */

export class Session {
  constructor(data) {
    this.id = data.id;
    this.userId = data.userId;
    this.courseId = data.courseId;
    this.sectionId = data.sectionId;
    this.startTime = data.startTime || new Date().toISOString();
    this.endTime = data.endTime || null;
    this.isComplete = data.isComplete || false;
    this.questionsAnswered = data.questionsAnswered || [];
    this.warmUpQuestions = data.warmUpQuestions || [];
    this.currentQuestionId = data.currentQuestionId || null;
    this.performanceMetrics = data.performanceMetrics || {
      totalQuestions: 0,
      correctAnswers: 0,
      averageTime: 0,
      hintsUsed: 0,
      struggledQuestions: [],
    };
  }

  /**
   * Add answered question
   */
  addAnsweredQuestion(questionId, isCorrect, timeSpent, hintsUsed) {
    this.questionsAnswered.push({
      questionId,
      isCorrect,
      timeSpent,
      hintsUsed,
      timestamp: new Date().toISOString(),
    });

    // Update metrics
    this.performanceMetrics.totalQuestions++;
    if (isCorrect) {
      this.performanceMetrics.correctAnswers++;
    } else {
      if (!this.performanceMetrics.struggledQuestions.includes(questionId)) {
        this.performanceMetrics.struggledQuestions.push(questionId);
      }
    }
    this.performanceMetrics.hintsUsed += hintsUsed;
    
    // Update average time
    const totalTime = this.questionsAnswered.reduce((sum, q) => sum + q.timeSpent, 0);
    this.performanceMetrics.averageTime = totalTime / this.questionsAnswered.length;
  }

  /**
   * Complete the session
   */
  complete() {
    this.isComplete = true;
    this.endTime = new Date().toISOString();
  }

  /**
   * Get session duration in minutes
   */
  getDuration() {
    if (!this.endTime) {
      return Math.floor((Date.now() - new Date(this.startTime).getTime()) / 60000);
    }
    return Math.floor((new Date(this.endTime).getTime() - new Date(this.startTime).getTime()) / 60000);
  }

  /**
   * Get accuracy percentage
   */
  getAccuracy() {
    if (this.performanceMetrics.totalQuestions === 0) return 0;
    return (this.performanceMetrics.correctAnswers / this.performanceMetrics.totalQuestions) * 100;
  }

  /**
   * Convert to JSON
   */
  toJSON() {
    return {
      id: this.id,
      userId: this.userId,
      courseId: this.courseId,
      sectionId: this.sectionId,
      startTime: this.startTime,
      endTime: this.endTime,
      isComplete: this.isComplete,
      questionsAnswered: this.questionsAnswered,
      warmUpQuestions: this.warmUpQuestions,
      currentQuestionId: this.currentQuestionId,
      performanceMetrics: this.performanceMetrics,
    };
  }

  /**
   * Create from JSON
   */
  static fromJSON(json) {
    return new Session(json);
  }
}
